import { noop } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import EcDatepicker from '~/components/Shared/EcDatepicker/EcDatepicker';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Nullable } from '~/eds/src/types/Nullable';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DataFieldType } from '~/enums/DataFieldType';
import { FieldValueSelect } from '~/features/fields/FieldValueSelect/FieldValueSelect';
import { DataFieldValue } from '~/features/fields/types';
import { DocumentInformationField } from '~/features/search/types/bulk-actions';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { parseUserDate } from '~/i18n/utils';

interface Props<T extends DataFieldType> {
  field: DocumentInformationField;
  onChange: (updatedValue: Nullable<DataFieldValue<T>>) => void;
  value: Nullable<DataFieldValue<T>>;
}
export const FieldEdit = <T extends DataFieldType>({
  field,
  onChange,
  value,
}: Props<T>) => {
  const intl = useIntl();
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );

  //TODO: remove when EKP-23791 is implemented.
  if (field.type === DataFieldType.DATE) {
    return (
      <Layout preset="form-fields">
        <Layout direction="column">
          <Label>{field.label}</Label>
          <Text preset="description">
            {intl.formatMessage({
              id: 'FieldEdit.selectDateValue',
              defaultMessage: 'Select the value(s) you want to use',
              description: 'Description for date field selection',
            })}
          </Text>
        </Layout>

        {hasLocalization ? (
          <DateInput
            width="100%"
            name={field.label}
            value={value ? parseUserDate(value as Date) : null}
            onChange={(date) => {
              onChange(date ? (date as unknown as DataFieldValue<T>) : null);
            }}
          />
        ) : (
          <EcDatepicker
            onDateChange={onChange}
            date={value ? (parseDate(value) as Date) : null}
            title={field.label}
            placeholder={intl.formatMessage({
              id: 'FieldEdit.selectFieldValue',
              defaultMessage: 'Select field value',
              description: 'Placeholder for field value selection',
            })}
          />
        )}
      </Layout>
    );
  }
  if (field.type === DataFieldType.NUMBER) {
    return (
      <FormField
        label={field.label}
        description={intl.formatMessage({
          id: 'FieldEdit.enterNumberDescription',
          defaultMessage: 'Enter the number you want to use',
          description: 'Description for number field input',
        })}
        name={field.label}
        input={NumberInput}
        inputProps={{
          name: field.label,
          width: '100%',
        }}
        value={value as number}
        onChange={(updatedValue) =>
          onChange(updatedValue as Nullable<DataFieldValue<T>>)
        }
      />
    );
  }

  const { input, inputProps, effectiveType } = getInput(
    field,
    intl,
    isWSSIntegrationPhase2M1M3Enabled,
  );
  const description = getDescription(effectiveType, intl);

  return (
    <FormField<DataFieldValue<T>, false>
      label={field.label}
      description={description}
      name={field.label}
      input={input}
      inputProps={inputProps}
      value={value}
      onChange={onChange}
    />
  );
};

const getDescription = (type: DataFieldType, intl: IntlShape) => {
  switch (type) {
    case DataFieldType.ARRAY_MULTIPLE:
    case DataFieldType.ARRAY_SINGLE:
      return intl.formatMessage({
        id: 'FieldEdit.selectValuesDescription',
        defaultMessage: 'Select the value(s) you want to use',
        description: 'Description for array field selection',
      });
    case DataFieldType.ATTACHMENT:
      return intl.formatMessage({
        id: 'FieldEdit.selectFileDescription',
        defaultMessage: 'Select the file you want to use',
        description: 'Description for attachment field selection',
      });
    case DataFieldType.BOOLEAN:
      return intl.formatMessage({
        id: 'FieldEdit.selectValueDescription',
        defaultMessage: 'Select the value you want to use',
        description: 'Description for boolean field selection',
      });
    case DataFieldType.DATE:
      return intl.formatMessage({
        id: 'FieldEdit.selectDateDescription',
        defaultMessage: 'Select the date you want to use',
        description: 'Description for date field selection',
      });
    case DataFieldType.NUMBER:
      return intl.formatMessage({
        id: 'FieldEdit.enterNumberDescription',
        defaultMessage: 'Enter the number you want to use',
        description: 'Description for number field input',
      });
    case DataFieldType.TEXT_AREA:
      return intl.formatMessage({
        id: 'FieldEdit.enterTextAreaDescription',
        defaultMessage: 'Enter the text you want to use',
        description: 'Description for text area field input',
      });
    case DataFieldType.STRING:
    default:
      return intl.formatMessage({
        id: 'FieldEdit.enterTextDescription',
        defaultMessage: 'Enter the text you want to use',
        description: 'Description for string field input',
      });
  }
};

const getInput = (
  field: DocumentInformationField,
  intl: IntlShape,
  isWSSIntegrationPhase2M1M3Enabled: boolean,
) => {
  const { type, id, staticKeyName } = field;

  let input;
  let inputProps;

  const isContractDescription =
    isWSSIntegrationPhase2M1M3Enabled &&
    staticKeyName === 'contract_description' &&
    type === DataFieldType.STRING;
  const effectiveType = isContractDescription ? DataFieldType.TEXT_AREA : type;

  switch (effectiveType) {
    case DataFieldType.ARRAY_MULTIPLE:
    case DataFieldType.ARRAY_SINGLE: {
      input = FieldValueSelect;
      inputProps = {
        enableSelectAll: false,
        isMulti: type === DataFieldType.ARRAY_MULTIPLE,
        placeholder: intl.formatMessage({
          id: 'FieldEdit.selectFieldValuePlaceholder',
          defaultMessage: 'Select field value',
          description: 'Placeholder for field value selection',
        }),
        fieldId: id,
        onAddOption: field.allowNewOptions ? noop : undefined,
      };
      break;
    }
    case DataFieldType.ATTACHMENT:
      input = null;
      break;
    case DataFieldType.BOOLEAN:
      input = RadioGroup;
      inputProps = {
        options: [
          {
            label: intl.formatMessage({
              id: 'FieldEdit.trueOption',
              defaultMessage: 'True',
              description: 'Boolean true option',
            }),
            value: true,
          },
          {
            label: intl.formatMessage({
              id: 'FieldEdit.falseOption',
              defaultMessage: 'False',
              description: 'Boolean false option',
            }),
            value: false,
          },
        ],
      };
      break;
    // TODO: DataFieldType.CLAUSE to be supported
    case DataFieldType.NUMBER:
      input = NumberInput;
      break;
    case DataFieldType.TEXT_AREA:
      input = TextArea;
      break;
    case DataFieldType.STRING:
    default:
      input = TextInput;
      break;
  }

  return {
    input,
    inputProps,
    effectiveType,
  };
};
